/**
 * Kid-friendly inline SVG icons for inventory / forge UI.
 * Colors follow the item catalog accent via CSS --icon-color.
 */

import { ITEM_CATALOG, ItemId } from '../items/catalog';

const ICONS: Record<ItemId, string> = {
  [ItemId.Dirt]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <ellipse cx="32" cy="48" rx="26" ry="10" fill="currentColor" opacity="0.35"/>
      <path d="M10 44c4-16 12-28 22-28s18 12 22 28c-6 6-14 9-22 9s-16-3-22-9z" fill="currentColor"/>
      <circle cx="22" cy="38" r="3" fill="#5a3a1a" opacity="0.45"/>
      <circle cx="36" cy="42" r="2.5" fill="#5a3a1a" opacity="0.4"/>
    </svg>`,
  [ItemId.Stone]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path d="M14 40 L22 18 L40 14 L52 28 L48 48 L24 52 Z" fill="currentColor"/>
      <path d="M22 18 L28 30 L40 14" fill="none" stroke="#fff" stroke-opacity="0.25" stroke-width="2"/>
      <path d="M28 30 L48 48" fill="none" stroke="#000" stroke-opacity="0.2" stroke-width="2"/>
    </svg>`,
  [ItemId.IronOre]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path d="M18 44 L26 16 L38 16 L46 44 Z" fill="currentColor"/>
      <rect x="16" y="42" width="32" height="10" rx="2" fill="currentColor" opacity="0.85"/>
      <path d="M26 16 L30 42" fill="none" stroke="#fff" stroke-opacity="0.3" stroke-width="2"/>
    </svg>`,
  [ItemId.GoldOre]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path d="M32 10 L44 28 L32 54 L20 28 Z" fill="currentColor"/>
      <path d="M32 10 L38 28 L32 40 L26 28 Z" fill="#fff" opacity="0.28"/>
      <circle cx="32" cy="30" r="5" fill="#fff" opacity="0.35"/>
    </svg>`,
  [ItemId.PickaxeRusty]: pickSvg('#8a5a3a'),
  [ItemId.PickaxeIron]: pickSvg('#9aa4b0'),
  [ItemId.PickaxeSteel]: pickSvg('#c5d0dc'),
  [ItemId.PickaxeCarbide]: pickSvg('#6ec6ff'),
  [ItemId.PickaxePlasma]: pickSvg('#7dffb3'),
  [ItemId.SwordRusty]: swordSvg('#8a6a4a'),
  [ItemId.SwordIron]: swordSvg('#b0b8c4'),
  [ItemId.SwordSteel]: swordSvg('#e8eef5'),
  [ItemId.MaceIron]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <rect x="28" y="26" width="8" height="30" rx="2" fill="#6e4524"/>
      <circle cx="32" cy="22" r="14" fill="currentColor"/>
      <circle cx="32" cy="22" r="6" fill="#fff" opacity="0.25"/>
      <circle cx="22" cy="18" r="3" fill="currentColor"/>
      <circle cx="42" cy="18" r="3" fill="currentColor"/>
      <circle cx="22" cy="28" r="3" fill="currentColor"/>
      <circle cx="42" cy="28" r="3" fill="currentColor"/>
    </svg>`,
  [ItemId.BlasterPlasma]: `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <rect x="8" y="26" width="36" height="14" rx="4" fill="currentColor"/>
      <rect x="40" y="28" width="16" height="10" rx="2" fill="#9b5de5"/>
      <rect x="14" y="40" width="10" height="12" rx="2" fill="#4a3560"/>
      <circle cx="20" cy="32" r="3" fill="#7dffb3"/>
      <path d="M56 33 L62 33" stroke="#7dffb3" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
};

function pickSvg(head: string): string {
  return `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <rect x="29" y="18" width="6" height="38" rx="2" fill="#6e4524"/>
      <path d="M12 22 L32 10 L52 22 L44 28 L32 20 L20 28 Z" fill="${head}"/>
      <path d="M32 10 L32 20" stroke="#fff" stroke-opacity="0.35" stroke-width="2"/>
    </svg>`;
}

function swordSvg(blade: string): string {
  return `
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path d="M30 8 L34 8 L36 38 L28 38 Z" fill="${blade}"/>
      <path d="M32 8 L32 38" stroke="#fff" stroke-opacity="0.35" stroke-width="1.5"/>
      <rect x="22" y="38" width="20" height="5" rx="1" fill="#c9a227"/>
      <rect x="29" y="43" width="6" height="12" rx="1" fill="#6e4524"/>
      <circle cx="32" cy="58" r="3" fill="#c9a227"/>
    </svg>`;
}

/** Short kid-friendly label (drops "Forge/Craft" prefixes). */
export function itemShortName(id: ItemId): string {
  return ITEM_CATALOG[id].name
    .replace(/^Forge\s+/i, '')
    .replace(/^Craft\s+/i, '');
}

export function recipeShortName(name: string): string {
  return name.replace(/^Forge\s+/i, '').replace(/^Craft\s+/i, '');
}

/** Inline icon markup sized for UI chips / cards. */
export function itemIconHtml(id: ItemId, sizePx = 40): string {
  const def = ITEM_CATALOG[id];
  const svg = ICONS[id] ?? ICONS[ItemId.Stone];
  return `<span class="item-icon" style="--icon-color:${def.color};width:${sizePx}px;height:${sizePx}px;color:var(--icon-color)">${svg}</span>`;
}
